import {
  DocumentVerdictSchema,
  type DocumentVerdict,
  type EvidenceDocument,
} from '@paguz/contracts';
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
} from '@paguz/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  BotIcon,
  FileTextIcon,
  MinusIcon,
  PlusIcon,
  RotateCcwIcon,
  UserCheckIcon,
} from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import type { AdminApplicationDetail } from '../../api/origination-schemas.ts';
import { Field, Select, Textarea } from '../../components/form-controls.tsx';
import { DataList, JsonBlock, Panel } from '../../components/page.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { DOCUMENT_STATUS, EVIDENCE_LABEL } from '../../lib/labels.ts';
import { useCan } from '../../lib/session.ts';
import { LEGIBILITY, RELEVANCE, VALIDITY } from './labels.ts';

const route = getRouteApi('/app/expedientes/$applicationId');

const VERDICT_LABEL: Readonly<Record<DocumentVerdict, string>> = {
  ACCEPTED: 'Aceptar',
  NEEDS_REPLACEMENT: 'Pedir reemplazo',
  REJECTED: 'Rechazar',
};

const docLabel = (d: EvidenceDocument) =>
  EVIDENCE_LABEL[d.declaredType ?? d.detectedType ?? 'OTHER'];

/** Documentos con miniatura, estado y revisión IA; visor con zoom y revisión del analista. */
export function DocumentsTab({ detail }: { detail: AdminApplicationDetail }) {
  const { doc } = route.useSearch();
  const navigate = route.useNavigate();
  const docs = detail.application.documents;
  const open = docs.find((d) => d.id === doc) ?? null;
  const setOpen = (id: string | undefined) => {
    void navigate({ search: (prev) => ({ ...prev, tab: 'documentos', doc: id }) });
  };

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {docs.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => {
              setOpen(d.id);
            }}
            className={cn(
              'flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-left shadow-xs transition hover:border-highlight focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:outline-none',
              d.status === 'SUPERSEDED' && 'opacity-70',
            )}
          >
            <div className="flex h-40 items-center justify-center overflow-hidden bg-muted">
              {d.downloadUrl ? (
                <img src={d.downloadUrl} alt="" className="h-full w-full object-cover object-top" />
              ) : (
                <FileTextIcon className="size-10 text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-1 flex-col gap-2 p-4">
              <div className="flex items-start justify-between gap-2">
                <span className="font-semibold text-foreground">{docLabel(d)}</span>
                <Badge variant={DOCUMENT_STATUS[d.status].tone}>
                  {DOCUMENT_STATUS[d.status].label}
                </Badge>
              </div>
              <ReviewChips review={d.review} />
              <span className="text-xs text-muted-foreground">
                {formatDateTime(d.createdAt)} · {d.pageCount ?? 1} pág. ·{' '}
                {d.uploadedVia === 'CHAT' ? 'subido en el chat' : d.uploadedVia}
              </span>
            </div>
          </button>
        ))}
      </div>
      <DocumentViewer
        detail={detail}
        doc={open}
        onClose={() => {
          setOpen(undefined);
        }}
      />
    </>
  );
}

function ReviewChips({ review }: { review: EvidenceDocument['review'] }) {
  if (!review)
    return <span className="text-xs text-muted-foreground">Sin revisión IA todavía</span>;
  const errors = review.issues.filter((i) => i.severity !== 'INFO').length;
  return (
    <div className="flex flex-wrap gap-1.5 text-xs">
      <Badge variant="outline">
        {review.reviewerType === 'AGENT' ? <BotIcon /> : <UserCheckIcon />}
        {review.reviewerType === 'AGENT' ? 'Revisión IA' : 'Analista'}
      </Badge>
      <Badge variant={review.legibility === 'POOR' ? 'danger' : 'secondary'}>
        Legibilidad: {LEGIBILITY[review.legibility]}
      </Badge>
      {errors > 0 && (
        <Badge variant="warning">
          <AlertTriangleIcon /> {errors} {errors === 1 ? 'observación' : 'observaciones'}
        </Badge>
      )}
      {review.fraudSignals.length > 0 && <Badge variant="danger">Señal de alteración</Badge>}
    </div>
  );
}

function DocumentViewer({
  detail,
  doc,
  onClose,
}: {
  detail: AdminApplicationDetail;
  doc: EvidenceDocument | null;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);
  return (
    <Dialog
      open={doc !== null}
      onOpenChange={(o) => {
        if (!o) {
          setZoom(1);
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[92vh] max-w-[min(1200px,94vw)] overflow-y-auto sm:max-w-[min(1200px,94vw)]">
        {doc && (
          <>
            <DialogHeader>
              <DialogTitle>{docLabel(doc)}</DialogTitle>
              <DialogDescription>
                {doc.mimeType} · {(doc.sizeBytes / 1024).toFixed(0)} KB · cargado{' '}
                {formatDateTime(doc.createdAt)}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label="Alejar"
                    onClick={() => {
                      setZoom((z) => Math.max(0.5, z - 0.25));
                    }}
                  >
                    <MinusIcon />
                  </Button>
                  <span className="w-14 text-center text-sm tabular-nums">
                    {Math.round(zoom * 100)} %
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label="Acercar"
                    onClick={() => {
                      setZoom((z) => Math.min(3, z + 0.25));
                    }}
                  >
                    <PlusIcon />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setZoom(1);
                    }}
                  >
                    <RotateCcwIcon /> Ajustar
                  </Button>
                </div>
                <div className="h-[60vh] overflow-auto rounded-xl border border-border bg-muted">
                  {doc.downloadUrl ? (
                    <img
                      src={doc.downloadUrl}
                      alt={`Documento: ${docLabel(doc)}`}
                      style={{ width: `${zoom * 100}%` }}
                      className="max-w-none"
                    />
                  ) : (
                    <p className="p-6 text-sm text-muted-foreground">Sin vista previa.</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <ReviewDetail doc={doc} />
                <AnalystReview detail={detail} doc={doc} />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReviewDetail({ doc }: { doc: EvidenceDocument }) {
  const review = doc.review;
  if (!review)
    return <p className="text-sm text-muted-foreground">El agente aún no revisa este documento.</p>;
  return (
    <Panel
      title={review.reviewerType === 'AGENT' ? 'Revisión IA' : 'Revisión del analista'}
      description={`Versión ${review.version} · ${formatDateTime(review.reviewedAt)}`}
      bodyClassName="flex flex-col gap-4"
    >
      <DataList
        items={[
          { label: 'Tipo detectado', value: EVIDENCE_LABEL[review.detectedType] },
          {
            label: 'Veredicto',
            value: (
              <Badge variant={DOCUMENT_STATUS[review.verdict].tone}>
                {DOCUMENT_STATUS[review.verdict].label}
              </Badge>
            ),
          },
          { label: 'Legibilidad', value: LEGIBILITY[review.legibility] },
          { label: 'Vigencia', value: VALIDITY[review.validity] },
          { label: 'Relevancia', value: RELEVANCE[review.relevance] },
          {
            label: 'Señales de alteración',
            value: review.fraudSignals.length === 0 ? 'Ninguna' : review.fraudSignals.join(', '),
          },
        ]}
      />
      {review.issues.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold text-muted-foreground">Observaciones</p>
          <ul className="flex flex-col gap-1 text-sm">
            {review.issues.map((i) => (
              <li
                key={i.code}
                className={cn(i.severity === 'ERROR' ? 'text-destructive' : 'text-foreground')}
              >
                <AlertTriangleIcon className="mr-1 inline size-4" /> {i.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {review.suggestions.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold text-muted-foreground">Sugerencias</p>
          <ul className="list-disc pl-5 text-sm">
            {review.suggestions.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {Object.keys(review.extracted).length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold text-muted-foreground">Datos extraídos</p>
          <JsonBlock value={review.extracted} />
        </div>
      )}
    </Panel>
  );
}

function AnalystReview({ detail, doc }: { detail: AdminApplicationDetail; doc: EvidenceDocument }) {
  const canOperate = useCan('applications:operate');
  const queryClient = useQueryClient();
  const [verdict, setVerdict] = useState<DocumentVerdict>('ACCEPTED');
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () => api.reviewDocument(doc.id, { verdict, reason: reason.trim() }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.application(detail.application.id), next);
      setReason('');
      toast.success('Revisión registrada (sobrescribe la revisión IA)');
    },
    onError: (error) => toast.error(describeError(error)),
  });
  if (!canOperate || doc.status === 'SUPERSEDED') return null;
  return (
    <Panel
      title="Tu revisión"
      description="Sobrescribe la revisión IA y queda trazada en el historial."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Field label="Veredicto" htmlFor="doc-verdict">
          <Select
            id="doc-verdict"
            value={verdict}
            onChange={(e) => {
              setVerdict(e.target.value as DocumentVerdict);
            }}
          >
            {DocumentVerdictSchema.options.map((v) => (
              <option key={v} value={v}>
                {VERDICT_LABEL[v]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Motivo" htmlFor="doc-reason">
          <Textarea
            id="doc-reason"
            value={reason}
            placeholder="Por qué cambias el resultado…"
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </Field>
        <Button
          type="submit"
          variant="highlight"
          disabled={reason.trim().length < 5 || mutation.isPending}
        >
          Registrar revisión
        </Button>
      </form>
    </Panel>
  );
}
